/**
 * Typed selectors for the `system_health` domain (Phase A.2).
 *
 * Mirrors /api/health/system + /api/health/token. Replaces the 30s polls
 * on those endpoints from HealthStrip + TopBar.
 */
import { useStateStore } from "@/state/store";

export type HealthStatus = "healthy" | "degraded" | "unhealthy" | "unknown";

export interface HealthCheck {
  ok:      boolean;
  detail?: string;
  error?:  string;
  [k: string]: unknown;
}

export interface TokenExpiry {
  authenticated:    boolean;
  hours_remaining?: number;
  expires_at?:      string | null;
  auth_time?:       string | null;
}

export interface DbPool {
  size?:        number;
  checked_out?: number;
  overflow?:    number;
  [k: string]:  unknown;
}

export interface SystemHealthSnapshot {
  as_of:        string;
  status:       HealthStatus;
  checked_at:   string;
  checks:       Record<string, HealthCheck>;
  token_expiry: TokenExpiry;
  db_pool:      DbPool;
  _seq?:        number;
}

export function useSystemHealth(): SystemHealthSnapshot | null {
  return useStateStore((s) => s.system_health.snapshot as SystemHealthSnapshot | null);
}

export function useHealthStatus(): HealthStatus {
  return useStateStore((s) => {
    const snap = s.system_health.snapshot as SystemHealthSnapshot | null;
    return snap?.status ?? "unknown";
  });
}

export function useTokenInfo(): TokenExpiry | null {
  return useStateStore((s) => {
    const snap = s.system_health.snapshot as SystemHealthSnapshot | null;
    return snap?.token_expiry ?? null;
  });
}

export function useDbPool(): DbPool | null {
  return useStateStore((s) => {
    const snap = s.system_health.snapshot as SystemHealthSnapshot | null;
    return snap?.db_pool ?? null;
  });
}

export function useSystemHealthIsStale(): boolean {
  return useStateStore((s) => s.system_health.isStale);
}

export function useSystemHealthLastUpdated(): number {
  return useStateStore((s) => s.system_health.lastUpdatedAt);
}
